import type {
  CreateReportBody,
  CreateReportResponse,
  DeleteReportParams,
  DeleteReportResponse,
  GetReportsAvailableResponse,
  GetReportsParams,
  GetReportsResponse,
} from '../types/reportsService.types';

import { useMocks } from '@/hooks/useMocks';
import useAxiosHTTPRequest from '@/Services/useAxiosHTTPRequest';
import { withMockSupportFn } from '@/utils/mockHelpers';

import {
  mockCreateReport,
  mockDeleteReport,
  mockDevicesActivatedCountResponse,
  mockDevicesActivatedResponse,
  mockGetReports,
  mockReportsAvailable,
} from '../mocks/reports.mocks';

type DevicesActivatedParams = {
  device_name: string;
  remote_id: string;
};

type Results = {
  device_id: number;
  channels: number;
  activated_at: Date;
  remote_id: string[];
  name: string[];
  channels_activated: boolean[];
  display_address: string;
  is_pre_activation: boolean;
};

interface GetDevicesActivatedResponse {
  results: Results[];
  too_many_results: boolean;
}

interface GetDevicesActivatedCountResponse {
  device_ids: number[];
  count: number;
}

interface ReportListService {
  getReports: (params: GetReportsParams) => Promise<GetReportsResponse>;
  getReportsAvailables: () => Promise<GetReportsAvailableResponse['rows']>;
  createReport: (body: CreateReportBody) => Promise<CreateReportResponse>;
  deleteReport: ({ id }: DeleteReportParams) => Promise<DeleteReportResponse>;
  devicesActivated: ({
    device_name,
    remote_id,
  }: DevicesActivatedParams) => Promise<GetDevicesActivatedResponse>;
  devicesActivatedCount: ({
    device_name,
    remote_id,
  }: DevicesActivatedParams) => Promise<GetDevicesActivatedCountResponse>;
}

export const useReportsService = (): ReportListService => {
  const { httpPrivate } = useAxiosHTTPRequest();
  const { shouldUseMocks } = useMocks();

  function getReports({ signal, ...params }: GetReportsParams): Promise<GetReportsResponse> {
    return withMockSupportFn(
      'sonarprint-reports',
      async () => {
        const response = await httpPrivate.get<GetReportsResponse>('/reports', {
          params,
          signal,
        });
        return response.data;
      },
      () => mockGetReports(params),
      { shouldUseMocksFn: shouldUseMocks },
    );
  }

  function getReportsAvailables(): Promise<GetReportsAvailableResponse['rows']> {
    return withMockSupportFn(
      'sonarprint-reports',
      async () => {
        const response = await httpPrivate.get<GetReportsAvailableResponse>(
          '/reports/available-reports',
        );
        return response.data.rows;
      },
      () => [...mockReportsAvailable],
      { shouldUseMocksFn: shouldUseMocks },
    );
  }

  function createReport({ name, type, params }: CreateReportBody): Promise<CreateReportResponse> {
    return withMockSupportFn(
      'sonarprint-reports',
      async () => {
        const response = await httpPrivate.post('/reports', {
          name,
          type,
          params: params ?? {},
        });
        return response.data;
      },
      () => mockCreateReport({ name, type, params }),
      { shouldUseMocksFn: shouldUseMocks },
    );
  }

  function deleteReport({ id }: DeleteReportParams): Promise<DeleteReportResponse> {
    return withMockSupportFn(
      'sonarprint-reports',
      async () => {
        const response = await httpPrivate.delete(`/reports/report/${id}`);
        return response.data;
      },
      () => mockDeleteReport(id),
      { shouldUseMocksFn: shouldUseMocks },
    );
  }

  function devicesActivated({
    device_name,
    remote_id,
  }: DevicesActivatedParams): Promise<GetDevicesActivatedResponse> {
    return withMockSupportFn(
      'sonarprint-reports',
      async () => {
        const response = await httpPrivate.get('/device-activation/activated', {
          params: { device_name, remote_id },
        });
        return response.data;
      },
      () => ({ ...mockDevicesActivatedResponse }),
      { shouldUseMocksFn: shouldUseMocks },
    );
  }

  function devicesActivatedCount({
    device_name,
    remote_id,
  }: DevicesActivatedParams): Promise<GetDevicesActivatedCountResponse> {
    return withMockSupportFn(
      'sonarprint-reports',
      async () => {
        const response = await httpPrivate.get('/device-activation/count', {
          params: { device_name, remote_id },
        });
        return response.data;
      },
      () => ({ ...mockDevicesActivatedCountResponse }),
      { shouldUseMocksFn: shouldUseMocks },
    );
  }

  return {
    getReports,
    getReportsAvailables,
    createReport,
    deleteReport,
    devicesActivated,
    devicesActivatedCount,
  };
};
